import { useState, useEffect } from "react";
import { Sidebar, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { getTodayStr, parseDateLocal } from "@/lib/utils";
import { 
  Plus, 
  Receipt, 
  Pencil, 
  Trash2, 
  CreditCard,
  Home,
  Car,
  Zap,
  Wifi,
  ShoppingBag,
  Heart,
  MoreHorizontal,
  Pause,
  Play,
  XCircle,
  Calendar,
  Repeat,
  AlertTriangle,
  ChevronRight,
  Check,
  Target,
  Link2
} from "lucide-react";

const CATEGORIES = [
  { value: "housing", label: "Housing", icon: Home },
  { value: "utilities", label: "Utilities", icon: Zap },
  { value: "transport", label: "Transport", icon: Car },
  { value: "subscriptions", label: "Subscriptions", icon: Wifi },
  { value: "food", label: "Food & Groceries", icon: ShoppingBag },
  { value: "entertainment", label: "Entertainment", icon: ShoppingBag },
  { value: "healthcare", label: "Healthcare", icon: Heart },
  { value: "insurance", label: "Insurance", icon: Heart },
  { value: "debt", label: "Debt Payment", icon: CreditCard },
  { value: "other", label: "Other", icon: MoreHorizontal }
];

const FREQUENCIES = [
  { value: "once", label: "One-off (just once)" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" }
];

export default function BillsPage() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [debtsData, setDebtsData] = useState({ debts: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [manageSheetOpen, setManageSheetOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [adjustDebtOpen, setAdjustDebtOpen] = useState(false);
  const [adjustingDebt, setAdjustingDebt] = useState(null);

  const currency = user?.currency || 'GBP';

  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    category: "other",
    frequency: "monthly",
    start_date: getTodayStr(),
    is_debt: false,
    total_debt: "",
    interest_rate: "",
    target_payoff_date: "",
    notes: "",
    is_recurring: true,
    linked_debt_id: ""  // Link this payment to an existing debt
  });

  const [adjustFormData, setAdjustFormData] = useState({
    total_debt: "",
    amount_paid: "",
    target_payoff_date: ""
  });

  useEffect(() => {
    fetchBills();
    fetchDebts();
  }, []);

  const fetchBills = async () => {
    try {
      const response = await axios.get(`${API}/bills?include_cancelled=false`, { withCredentials: true });
      setBills(response.data);
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchDebts = async () => {
    try {
      const response = await axios.get(`${API}/debts`, { withCredentials: true });
      setDebtsData(response.data);
    } catch (error) {
      console.error('Failed to load debts:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      amount: "",
      category: "other",
      frequency: "monthly",
      start_date: getTodayStr(),
      is_debt: false,
      total_debt: "",
      interest_rate: "",
      target_payoff_date: "",
      notes: "",
      is_recurring: true,
      linked_debt_id: ""
    });
    setEditingBill(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      currency: currency,
      frequency: formData.is_recurring ? formData.frequency : 'once',
      total_debt: formData.is_debt && formData.total_debt ? parseFloat(formData.total_debt) : null,
      interest_rate: formData.is_debt && formData.interest_rate ? parseFloat(formData.interest_rate) : null,
      target_payoff_date: formData.is_debt && formData.target_payoff_date ? formData.target_payoff_date : null,
      // Include linked_debt_id (can be null to clear the link)
      linked_debt_id: formData.linked_debt_id || null
    };

    try {
      if (editingBill) {
        await axios.put(`${API}/bills/${editingBill.bill_id}`, payload, { withCredentials: true });
        toast.success('Bill updated');
      } else {
        await axios.post(`${API}/bills`, payload, { withCredentials: true });
        toast.success('Bill added');
      }
      setDialogOpen(false);
      resetForm();
      fetchBills();
      fetchDebts();
    } catch (error) {
      toast.error('Failed to save bill');
    }
  };

  const handleEdit = (bill) => {
    setFormData({
      name: bill.name,
      amount: bill.amount.toString(),
      category: bill.category,
      frequency: bill.frequency,
      start_date: bill.start_date,
      is_debt: bill.is_debt,
      total_debt: bill.total_debt?.toString() || "",
      interest_rate: bill.interest_rate?.toString() || "",
      target_payoff_date: bill.target_payoff_date || "",
      notes: bill.notes || "",
      is_recurring: bill.is_recurring !== false && bill.frequency !== 'once',
      linked_debt_id: bill.linked_debt_id || ""
    });
    setEditingBill(bill);
    setManageSheetOpen(false);
    setDialogOpen(true);
  };

  const handleAdjustDebt = (debt) => {
    setAdjustingDebt(debt);
    setAdjustFormData({
      total_debt: debt.total_debt?.toString() || "",
      amount_paid: debt.amount_paid?.toString() || "0",
      target_payoff_date: debt.target_payoff_date || ""
    });
    setAdjustDebtOpen(true);
  };

  const submitAdjustment = async () => {
    if (!adjustingDebt) return;
    
    try {
      await axios.put(`${API}/debts/${adjustingDebt.bill_id}/adjust`, {
        total_debt: parseFloat(adjustFormData.total_debt) || adjustingDebt.total_debt,
        amount_paid: parseFloat(adjustFormData.amount_paid) || 0,
        target_payoff_date: adjustFormData.target_payoff_date || null
      }, { withCredentials: true });
      
      toast.success('Debt adjusted');
      setAdjustDebtOpen(false);
      setAdjustingDebt(null);
      fetchBills();
      fetchDebts();
    } catch (error) {
      toast.error('Failed to adjust debt');
    }
  };

  const markDebtPaidOff = async (debtId) => {
    try {
      await axios.post(`${API}/debts/${debtId}/mark-paid-off`, {}, { withCredentials: true });
      toast.success('🎉 Congratulations! Debt marked as paid off!');
      fetchBills();
      fetchDebts();
    } catch (error) {
      toast.error('Failed to mark debt as paid off');
    }
  };

  const handleDelete = async (billId) => {
    try {
      await axios.delete(`${API}/bills/${billId}`, { withCredentials: true });
      toast.success('Bill deleted');
      setManageSheetOpen(false);
      setSelectedBill(null);
      fetchBills();
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const handlePause = async (bill) => {
    try {
      await axios.post(`${API}/bills/${bill.bill_id}/pause`, {}, { withCredentials: true });
      toast.success(`${bill.name} paused`);
      fetchBills();
      setManageSheetOpen(false);
    } catch (error) {
      toast.error('Failed to pause bill');
    }
  };

  const handleResume = async (bill) => {
    try {
      await axios.post(`${API}/bills/${bill.bill_id}/resume`, {}, { withCredentials: true });
      toast.success(`${bill.name} resumed`);
      fetchBills();
      setManageSheetOpen(false);
    } catch (error) {
      toast.error('Failed to resume bill');
    }
  };

  const handleCancelFuture = async (bill) => {
    try {
      const cancelFrom = getTodayStr();
      await axios.post(`${API}/bills/${bill.bill_id}/cancel`, 
        { cancel_from: cancelFrom },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success(`Future payments for ${bill.name} cancelled`);
      setCancelDialogOpen(false);
      setManageSheetOpen(false);
      setSelectedBill(null);
      fetchBills();
    } catch (error) {
      toast.error('Failed to cancel future payments');
    }
  };

  const openManageSheet = (bill) => {
    setSelectedBill(bill);
    setManageSheetOpen(true);
  };

  const filteredBills = bills.filter(bill => {
    // Don't show cancelled bills
    if (bill.status === 'cancelled') return false;
    
    if (filter === 'all') return true;
    if (filter === 'debt') return bill.is_debt;
    if (filter === 'regular') return !bill.is_debt;
    if (filter === 'paused') return bill.status === 'paused';
    if (filter === 'oneoff') return bill.frequency === 'once';
    return bill.category === filter;
  });

  const totalMonthly = filteredBills.reduce((acc, bill) => {
    if (bill.status === 'paused') return acc;
    const multipliers = { daily: 30, weekly: 4.33, biweekly: 2.17, monthly: 1, yearly: 1/12, once: 0 };
    return acc + (bill.amount * (multipliers[bill.frequency] || 1));
  }, 0);

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : MoreHorizontal;
  };

  const getFrequencyLabel = (freq) => {
    const f = FREQUENCIES.find(item => item.value === freq);
    return f ? f.label : freq;
  };

  return (
    <Sidebar>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-manrope text-2xl sm:text-3xl font-bold text-p2p-teal">
            Bills & Expenses
          </h1>
          <p className="text-p2p-muted text-sm mt-1">
            Manage your recurring and one-off payments
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }} modal={true}>
          <DialogTrigger asChild>
            <Button className="btn-gold rounded-full shadow-lg shadow-p2p-gold/20" data-testid="add-bill-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Bill
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-p2p-border text-p2p-teal max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl">
                {editingBill ? 'Edit Bill' : 'Add New Bill'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-p2p-muted">Bill Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="h-11 rounded-xl border-p2p-border"
                  placeholder="e.g., Netflix, Rent"
                  required
                  data-testid="bill-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-p2p-muted">Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-p2p-muted">
                      {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'}
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="h-11 pl-7 rounded-xl border-p2p-border"
                      placeholder="0.00"
                      required
                      data-testid="bill-amount-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-p2p-muted">Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}>
                    <SelectTrigger className="h-11 rounded-xl border-p2p-border" data-testid="bill-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Recurring Toggle */}
              <div className="p-4 rounded-xl bg-p2p-surface-hover border border-p2p-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {formData.is_recurring ? (
                      <div className="w-9 h-9 rounded-lg bg-p2p-teal/10 flex items-center justify-center">
                        <Repeat className="w-4 h-4 text-p2p-teal" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-amber-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-p2p-teal text-sm">
                        {formData.is_recurring ? 'Recurring' : 'One-off'}
                      </p>
                      <p className="text-xs text-p2p-muted">
                        {formData.is_recurring ? 'Repeats automatically' : 'Just this once'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
                    data-testid="toggle-recurring"
                  />
                </div>

                {formData.is_recurring && (
                  <div className="mt-3 pt-3 border-t border-p2p-border/50">
                    <Label className="text-xs text-p2p-muted mb-1.5 block">How often?</Label>
                    <Select value={formData.frequency} onValueChange={(val) => setFormData(prev => ({ ...prev, frequency: val }))}>
                      <SelectTrigger className="h-10 rounded-lg border-p2p-border bg-white" data-testid="bill-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.filter(f => f.value !== 'once').map(freq => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-p2p-muted">
                  {formData.is_recurring ? 'First payment date' : 'Date'}
                </Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="h-11 rounded-xl border-p2p-border"
                  required
                  data-testid="bill-date-input"
                />
              </div>

              {/* Debt Toggle */}
              <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                <Switch
                  checked={formData.is_debt}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_debt: checked }))}
                  data-testid="bill-is-debt-switch"
                />
                <div>
                  <p className="text-sm font-medium text-p2p-teal">This is a debt payment</p>
                  <p className="text-xs text-p2p-muted">Track total debt and interest</p>
                </div>
              </div>

              {formData.is_debt && (
                <div className="grid grid-cols-2 gap-4 animate-fade-in">
                  <div className="space-y-2">
                    <Label className="text-p2p-muted text-sm">Total owed</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.total_debt}
                      onChange={(e) => setFormData(prev => ({ ...prev, total_debt: e.target.value }))}
                      className="h-10 rounded-lg border-p2p-border"
                      placeholder="0.00"
                      data-testid="bill-total-debt-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-p2p-muted text-sm">Interest rate</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.interest_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, interest_rate: e.target.value }))}
                        className="h-10 pr-8 rounded-lg border-p2p-border"
                        placeholder="0.0"
                        data-testid="bill-interest-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-p2p-muted text-sm">%</span>
                    </div>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label className="text-p2p-muted text-sm">Target Payoff Date (optional)</Label>
                    <Input
                      type="date"
                      value={formData.target_payoff_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, target_payoff_date: e.target.value }))}
                      className="h-10 rounded-lg border-p2p-border"
                      data-testid="bill-target-date-input"
                    />
                  </div>
                </div>
              )}

              {/* Link to Existing Debt - only show if this is NOT a debt definition and there are debts */}
              {!formData.is_debt && debtsData.debts && debtsData.debts.length > 0 && (
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Link2 className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-p2p-teal">Apply this payment to a debt</p>
                        <p className="text-xs text-p2p-muted">Track this as a debt repayment</p>
                      </div>
                    </div>
                    <Switch
                      checked={!!formData.linked_debt_id}
                      onCheckedChange={(checked) => setFormData(prev => ({ 
                        ...prev, 
                        linked_debt_id: checked ? (debtsData.debts[0]?.bill_id || "") : "" 
                      }))}
                      data-testid="link-to-debt-switch"
                    />
                  </div>
                  
                  {formData.linked_debt_id && (
                    <div className="mt-3 pt-3 border-t border-blue-200/50 animate-fade-in">
                      <Label className="text-xs text-p2p-muted mb-1.5 block">Which debt?</Label>
                      <Select 
                        value={formData.linked_debt_id} 
                        onValueChange={(val) => setFormData(prev => ({ ...prev, linked_debt_id: val }))}
                      >
                        <SelectTrigger className="h-10 rounded-lg border-blue-200 bg-white" data-testid="linked-debt-select">
                          <SelectValue placeholder="Select a debt" />
                        </SelectTrigger>
                        <SelectContent>
                          {debtsData.debts.map(debt => (
                            <SelectItem key={debt.bill_id} value={debt.bill_id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{debt.name}</span>
                                <span className="text-xs text-p2p-muted ml-2">
                                  {formatCurrency(debt.total_debt - (debt.amount_paid || 0), currency)} remaining
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-blue-600 mt-2">
                        When you mark this bill as paid, the debt balance will automatically update.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1 h-11 border-p2p-border"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 h-11 btn-gold" data-testid="save-bill-btn">
                  {editingBill ? 'Update' : 'Add'} Bill
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters and Summary */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            { key: 'regular', label: 'Regular' },
            { key: 'debt', label: 'Debts' },
            { key: 'oneoff', label: 'One-off' },
            { key: 'paused', label: 'Paused' }
          ].map((f) => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              className={filter === f.key 
                ? 'bg-p2p-teal text-white' 
                : 'border-p2p-border text-p2p-muted hover:bg-p2p-surface-hover'}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}-btn`}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="sm:ml-auto light-card px-4 py-2 rounded-xl">
          <span className="text-sm text-p2p-muted">Monthly: </span>
          <span className="font-mono font-bold text-p2p-teal">{formatCurrency(totalMonthly, currency)}</span>
        </div>
      </div>

      {/* Bills List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-p2p-teal border-t-transparent"></div>
        </div>
      ) : filteredBills.length === 0 ? (
        <div className="light-card p-12 rounded-2xl text-center">
          <div className="w-16 h-16 rounded-2xl bg-p2p-surface-hover flex items-center justify-center mx-auto mb-4">
            <Receipt className="w-8 h-8 text-p2p-muted" />
          </div>
          <h3 className="font-manrope text-xl font-semibold text-p2p-teal mb-2">No bills yet</h3>
          <p className="text-p2p-muted mb-6">Add your first bill to start tracking</p>
          <Button className="btn-gold rounded-full" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Your First Bill
          </Button>
        </div>
      ) : filter === 'debt' ? (
        /* Debt Dashboard View */
        <div className="space-y-6">
          {/* Debt-Free Summary */}
          {debtsData.summary?.total_remaining > 0 && (
            <div className="light-card p-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <Target className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-manrope text-xl font-bold text-emerald-700">Debt-Free Projection</h3>
                  <p className="text-emerald-600/80 text-sm">At your current payment rate</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 rounded-xl bg-white/60">
                  <p className="text-xs text-emerald-600/70 uppercase tracking-wide mb-1">Total Debt</p>
                  <p className="font-mono text-lg font-bold text-red-500">
                    {formatCurrency(debtsData.summary?.total_debt || 0, currency)}
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/60">
                  <p className="text-xs text-emerald-600/70 uppercase tracking-wide mb-1">Paid Off</p>
                  <p className="font-mono text-lg font-bold text-emerald-600">
                    {formatCurrency(debtsData.summary?.total_paid || 0, currency)}
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/60">
                  <p className="text-xs text-emerald-600/70 uppercase tracking-wide mb-1">Remaining</p>
                  <p className="font-mono text-lg font-bold text-amber-600">
                    {formatCurrency(debtsData.summary?.total_remaining || 0, currency)}
                  </p>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/60">
                  <p className="text-xs text-emerald-600/70 uppercase tracking-wide mb-1">Debt-Free By</p>
                  <p className="font-mono text-lg font-bold text-emerald-700">
                    {debtsData.summary?.debt_free_date 
                      ? parseDateLocal(debtsData.summary.debt_free_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
              </div>
              
              {debtsData.summary?.months_to_freedom && (
                <p className="text-center text-emerald-700 font-medium">
                  ✨ {Math.ceil(debtsData.summary.months_to_freedom)} months to go until you&apos;re debt-free!
                </p>
              )}
            </div>
          )}

          {/* Individual Debts */}
          <div className="space-y-4">
            <h3 className="font-manrope text-lg font-semibold text-p2p-teal">Your Debts</h3>
            
            {debtsData.debts.length === 0 ? (
              <div className="light-card p-8 rounded-2xl text-center">
                <CreditCard className="w-12 h-12 text-p2p-muted mx-auto mb-3" />
                <p className="text-p2p-muted">No debts tracked yet</p>
                <Button 
                  className="mt-4 btn-gold rounded-full"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, is_debt: true }));
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add a Debt
                </Button>
              </div>
            ) : (
              debtsData.debts.map((debt) => (
                <div key={debt.bill_id} className="light-card p-5 rounded-2xl" data-testid={`debt-card-${debt.bill_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-p2p-teal text-lg">{debt.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            debt.status === 'on_track' 
                              ? 'bg-emerald-100 text-emerald-600' 
                              : 'bg-red-100 text-red-600'
                          }`}>
                            {debt.status === 'on_track' ? '✓ On Track' : '⚠ Behind'}
                          </span>
                          {debt.target_payoff_date && (
                            <span className="text-xs text-p2p-muted">
                              Target: {parseDateLocal(debt.target_payoff_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleAdjustDebt(debt)}
                        data-testid={`adjust-debt-${debt.bill_id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Adjust
                      </Button>
                      {debt.remaining <= 0 ? (
                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-600 text-xs font-semibold">
                          🎉 Paid Off!
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => markDebtPaidOff(debt.bill_id)}
                        >
                          Mark Paid Off
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Section */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-2xl font-bold text-emerald-600">{debt.progress.toFixed(0)}%</span>
                        <span className="text-sm text-p2p-muted ml-2">complete</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-p2p-muted">Monthly payment</p>
                        <p className="font-mono font-semibold text-p2p-teal">{formatCurrency(debt.monthly_payment, currency)}</p>
                      </div>
                    </div>
                    
                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${debt.progress}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 rounded-xl bg-red-50">
                      <p className="text-xs text-red-400 mb-1">Total Debt</p>
                      <p className="font-mono font-bold text-red-600">{formatCurrency(debt.total_debt, currency)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-50">
                      <p className="text-xs text-emerald-400 mb-1">Paid So Far</p>
                      <p className="font-mono font-bold text-emerald-600">{formatCurrency(debt.amount_paid, currency)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50">
                      <p className="text-xs text-amber-400 mb-1">Remaining</p>
                      <p className="font-mono font-bold text-amber-600">{formatCurrency(debt.remaining, currency)}</p>
                    </div>
                  </div>
                  
                  {/* Projected Payoff */}
                  {debt.projected_payoff_date && debt.remaining > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-center text-p2p-muted">
                        At this rate, paid off by <span className="font-semibold text-emerald-600">
                          {parseDateLocal(debt.projected_payoff_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                        </span>
                        {debt.months_to_payoff && (
                          <span className="text-p2p-muted"> ({Math.ceil(debt.months_to_payoff)} months)</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBills.map((bill) => {
            const CategoryIcon = getCategoryIcon(bill.category);
            const isPaused = bill.status === 'paused';
            const isOneOff = bill.frequency === 'once';
            
            // Calculate debt progress
            const amountPaid = bill.amount_paid || 0;
            const totalDebt = bill.total_debt || 0;
            const debtProgress = totalDebt > 0 ? Math.min((amountPaid / totalDebt) * 100, 100) : 0;
            const remaining = totalDebt - amountPaid;
            
            return (
              <div 
                key={bill.bill_id}
                className={`light-card p-4 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                  isPaused ? 'opacity-60' : ''
                }`}
                onClick={() => openManageSheet(bill)}
                data-testid={`bill-item-${bill.bill_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    bill.is_debt ? 'bg-red-100' : 'bg-p2p-teal/10'
                  }`}>
                    <CategoryIcon className={`w-6 h-6 ${bill.is_debt ? 'text-red-500' : 'text-p2p-teal'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-p2p-teal truncate">{bill.name}</h3>
                      {bill.is_debt && (
                        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                          Debt
                        </span>
                      )}
                      {isPaused && (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 text-xs font-medium flex items-center gap-1">
                          <Pause className="w-3 h-3" /> Paused
                        </span>
                      )}
                      {isOneOff && (
                        <span className="px-2 py-0.5 rounded-full bg-p2p-surface-hover text-p2p-muted text-xs font-medium">
                          One-off
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-p2p-muted mt-0.5">
                      <span>{getFrequencyLabel(bill.frequency)}</span>
                      <span>•</span>
                      <span className="capitalize">{bill.category}</span>
                    </div>
                    
                    {/* Debt Progress Bar */}
                    {bill.is_debt && totalDebt > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-p2p-muted">
                            {formatCurrency(amountPaid, currency)} paid
                          </span>
                          <span className="text-emerald-600 font-medium">
                            {debtProgress.toFixed(0)}%
                          </span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${debtProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-p2p-muted mt-1">
                          {formatCurrency(remaining, currency)} remaining
                        </p>
                      </div>
                    )}
                    
                    {/* Linked Debt Indicator */}
                    {bill.linked_debt_id && !bill.is_debt && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-600">
                        <Link2 className="w-3 h-3" />
                        <span>Linked to {debtsData.debts.find(d => d.bill_id === bill.linked_debt_id)?.name || 'debt'}</span>
                      </div>
                    )}
                  </div>

                  <div className="text-right flex items-center gap-3">
                    <div>
                      <p className={`font-mono font-bold text-lg ${isPaused ? 'text-p2p-muted' : 'text-p2p-teal'}`}>
                        {formatCurrency(bill.amount, currency)}
                      </p>
                      {bill.is_debt && bill.total_debt && !bill.amount_paid && (
                        <p className="text-xs text-p2p-muted">of {formatCurrency(bill.total_debt, currency)}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-p2p-muted" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Manage Bill Sheet */}
      <Sheet open={manageSheetOpen} onOpenChange={setManageSheetOpen}>
        <SheetContent className="bg-white border-l-p2p-border w-full sm:max-w-md">
          {selectedBill && (
            <>
              <SheetHeader className="text-left pb-6 border-b border-p2p-border">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    selectedBill.is_debt ? 'bg-red-100' : 'bg-p2p-teal/10'
                  }`}>
                    {(() => {
                      const Icon = getCategoryIcon(selectedBill.category);
                      return <Icon className={`w-7 h-7 ${selectedBill.is_debt ? 'text-red-500' : 'text-p2p-teal'}`} />;
                    })()}
                  </div>
                  <div>
                    <SheetTitle className="font-manrope text-xl text-p2p-teal">
                      {selectedBill.name}
                    </SheetTitle>
                    <p className="text-sm text-p2p-muted mt-0.5">
                      {getFrequencyLabel(selectedBill.frequency)} • {selectedBill.category}
                    </p>
                  </div>
                </div>
              </SheetHeader>

              <div className="py-6 space-y-6">
                {/* Amount Display */}
                <div className="text-center py-4">
                  <p className="text-sm text-p2p-muted mb-1">Amount</p>
                  <p className="font-mono text-4xl font-bold text-p2p-teal">
                    {formatCurrency(selectedBill.amount, currency)}
                  </p>
                  {selectedBill.is_debt && selectedBill.total_debt && (
                    <p className="text-sm text-p2p-muted mt-2">
                      Total debt: {formatCurrency(selectedBill.total_debt, currency)}
                      {selectedBill.interest_rate && ` at ${selectedBill.interest_rate}% APR`}
                    </p>
                  )}
                </div>

                {/* Status */}
                {selectedBill.status === 'paused' && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <Pause className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-700">Currently paused</p>
                      <p className="text-sm text-amber-600">This bill won&apos;t appear in your calendar</p>
                    </div>
                  </div>
                )}

                {/* Linked Debt Display */}
                {selectedBill.linked_debt_id && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <Link2 className="w-5 h-5 text-blue-500" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-700">Linked to debt repayment</p>
                      <p className="text-sm text-blue-600">
                        {debtsData.debts.find(d => d.bill_id === selectedBill.linked_debt_id)?.name || 'Debt'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-p2p-muted uppercase tracking-wider mb-3">Actions</p>
                  
                  {/* Edit */}
                  <button
                    onClick={() => handleEdit(selectedBill)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-p2p-border hover:bg-p2p-surface-hover transition-colors text-left"
                    data-testid="edit-bill-action"
                  >
                    <div className="w-10 h-10 rounded-lg bg-p2p-teal/10 flex items-center justify-center">
                      <Pencil className="w-5 h-5 text-p2p-teal" />
                    </div>
                    <div>
                      <p className="font-medium text-p2p-teal">Edit bill</p>
                      <p className="text-sm text-p2p-muted">Change amount, frequency, or details</p>
                    </div>
                  </button>

                  {/* Pause/Resume - only for recurring bills */}
                  {selectedBill.frequency !== 'once' && (
                    selectedBill.status === 'paused' ? (
                      <button
                        onClick={() => handleResume(selectedBill)}
                        className="w-full flex items-center gap-4 p-4 rounded-xl border border-p2p-border hover:bg-emerald-50 transition-colors text-left"
                        data-testid="resume-bill-action"
                      >
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Play className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-emerald-600">Resume bill</p>
                          <p className="text-sm text-p2p-muted">Start tracking this bill again</p>
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePause(selectedBill)}
                        className="w-full flex items-center gap-4 p-4 rounded-xl border border-p2p-border hover:bg-amber-50 transition-colors text-left"
                        data-testid="pause-bill-action"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Pause className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-amber-600">Pause temporarily</p>
                          <p className="text-sm text-p2p-muted">Hide from calendar but keep history</p>
                        </div>
                      </button>
                    )
                  )}

                  {/* Cancel Future - only for recurring bills */}
                  {selectedBill.frequency !== 'once' && selectedBill.status !== 'paused' && (
                    <button
                      onClick={() => setCancelDialogOpen(true)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border border-red-200 hover:bg-red-50 transition-colors text-left"
                      data-testid="cancel-future-action"
                    >
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <p className="font-medium text-red-600">Cancel future payments</p>
                        <p className="text-sm text-p2p-muted">Stop all upcoming occurrences</p>
                      </div>
                    </button>
                  )}

                  {/* Delete */}
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete ${selectedBill.name}? This cannot be undone.`)) {
                        handleDelete(selectedBill.bill_id);
                      }
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-p2p-border hover:bg-red-50 transition-colors text-left"
                    data-testid="delete-bill-action"
                  >
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-red-600">Delete completely</p>
                      <p className="text-sm text-p2p-muted">Remove this bill entirely</p>
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Cancel Future Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white border-p2p-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-xl text-p2p-teal flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Cancel Future Payments
            </DialogTitle>
            <DialogDescription className="text-p2p-muted">
              Are you sure you want to cancel all future payments for{' '}
              <span className="font-medium text-p2p-teal">{selectedBill?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>What happens:</strong>
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-700">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-emerald-500" />
                  Past payments stay in your history
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 mt-0.5 text-red-500" />
                  No more future occurrences
                </li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-p2p-border"
              onClick={() => setCancelDialogOpen(false)}
            >
              Keep Bill
            </Button>
            <Button 
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              onClick={() => handleCancelFuture(selectedBill)}
              data-testid="confirm-cancel-btn"
            >
              Cancel Future Payments
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Debt Dialog */}
      <Dialog open={adjustDebtOpen} onOpenChange={setAdjustDebtOpen}>
        <DialogContent className="bg-white border-p2p-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-xl text-p2p-teal">
              Adjust {adjustingDebt?.name}
            </DialogTitle>
            <DialogDescription className="text-p2p-muted">
              Manually adjust the balance if needed for real-life corrections.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-p2p-muted">Total Debt</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-p2p-muted">
                  {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'}
                </span>
                <Input
                  type="number"
                  value={adjustFormData.total_debt}
                  onChange={(e) => setAdjustFormData(prev => ({ ...prev, total_debt: e.target.value }))}
                  className="pl-8 border-p2p-border"
                  data-testid="adjust-total-debt"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-p2p-muted">Amount Paid</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-p2p-muted">
                  {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'}
                </span>
                <Input
                  type="number"
                  value={adjustFormData.amount_paid}
                  onChange={(e) => setAdjustFormData(prev => ({ ...prev, amount_paid: e.target.value }))}
                  className="pl-8 border-p2p-border"
                  data-testid="adjust-amount-paid"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-p2p-muted">Target Payoff Date (Optional)</Label>
              <Input
                type="date"
                value={adjustFormData.target_payoff_date}
                onChange={(e) => setAdjustFormData(prev => ({ ...prev, target_payoff_date: e.target.value }))}
                className="mt-1 border-p2p-border"
                data-testid="adjust-target-date"
              />
            </div>
            
            {adjustFormData.total_debt && adjustFormData.amount_paid && (
              <div className="p-3 rounded-xl bg-emerald-50 text-center">
                <p className="text-sm text-emerald-600">
                  Remaining: <span className="font-bold">
                    {formatCurrency(
                      Math.max(0, parseFloat(adjustFormData.total_debt || 0) - parseFloat(adjustFormData.amount_paid || 0)),
                      currency
                    )}
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 border-p2p-border"
              onClick={() => setAdjustDebtOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              className="flex-1 btn-gold"
              onClick={submitAdjustment}
              data-testid="save-adjustment-btn"
            >
              Save Adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
